import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { ItemDropdownMenu, PODropdownMenu, ServiceThreadSpinner, SortHeader } from './';
import { api, constants, dateHelpers, helpers, UserContext } from '../utils';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function VendorCurrentOrders({ vendorId, vendorName }) {
   const userCtx = useContext(UserContext);
   const { currentUser } = userCtx;
   const [data, setData] = useState([]);
   const [sortController, setSortController] = useState({
      sortDirection: 'asc',
      sortField: ''
   });
   const [working, setWorking] = useState(false);

   useEffect(refreshData, [vendorId, sortController])

   const onSortChange = (fieldName, Direction) => {
      setSortController({
         sortDirection: Direction,
         sortField: fieldName
      });
   }

   const PrintToExcel = () => {
      let payload = {
         VendorId: vendorId,
         SortField: sortController.sortField,
         SortDirection: sortController.sortDirection
      };
      api
         .postGetBlob(
            `Purchasing/ExportToExcelVendorCurrentOrders`, payload
         )
         .then((r) => {
            if (r.data.success === false) {
               if (_.isString(r?.data?.message)) {
                  toast.error(r?.data?.message);
               }
               return;
            }
            if (r.data) {
               const XLSX_FILENAME = `CurrentVendorPOs.xlsx`;
               helpers.downloadExport(
                  r.data,
                  XLSX_FILENAME,
                  constants.MIME_TYPES.xlsx
               );
            } else {
               toast.error(r.message || "Error downloading report");
            }
         })
         .catch(api.catchHandler);
   }

   function refreshData() {
      let payload = {
         VendorId: vendorId,
         SortField: sortController.sortField,
         SortDirection: sortController.sortDirection
      };

      if (!vendorId) return toast.error("vendorId not found");
      setWorking(true);
      api
         .post(`Purchasing/GetPOInformation`, payload)
         .then(r => {
            if (r.data.success) {
               setData(r.data.message);
            } else {
               toast.error("Error getting Vendor PO Information")
               console.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   }

   return (
      <>
         <div>
            <span className="h2 me-2" children="Current Orders" />{" "}
            <span className="text-muted" >Vendor {vendorId}</span>
         </div>
         <hr />
         <ServiceThreadSpinner on={working}>
            {data && data.length
               ?
               <>
                  <Row className={"mb-2"}>
                     <Col
                        xs={{ offset: 6, size: 6 }}
                        className="transparent-background"
                     >
                        <Button
                           title="Download as .xlsx"
                           className="shredButtonExcel float-end ms-2"
                           onClick={PrintToExcel}
                           disabled={working}
                        >
                           <FontAwesomeIcon icon="file-excel" size="lg" />
                        </Button>
                     </Col>
                  </Row>
                  <Table size="sm" className="data-table">
                     <thead className="table-header">
                        <tr>
                           <th width="6%">
                              <SortHeader
                                 displayName={<span>Created<br />Date</span>}
                                 field="DocumentDate"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "DocumentDate"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th width="6%">
                              <SortHeader
                                 displayName={"PO #"}
                                 field="PONumber"
                                 sortDirection={sortController.sortDirection}
                                 sorted={sortController.sortField === "PONumber"}
                                 callback={onSortChange}
                              />
                           </th>
                           <th width="10%">Containers</th>
                           <th width="6%">Line<br />Status</th>
                           <th width="20%">Item</th>
                           <th width="29%">Vendor Item</th>
                           <th width="8%">Location</th>
                           <th width="5%">QTY<br />Ordered</th>
                           <th width="5%">QTY<br />Shipped</th>
                           <th width="5%">Current<br />Cost</th>
                           <th width="5%">Ext<br />Cost</th>
                           <th width="5%">Document<br />Status</th>
                        </tr>
                     </thead>
                     <tbody>
                        {_.map(data, (datum, i) => {
                           return (
                              <tr className={`tr-${i % 2 === 0 ? "primary" : "secondary"}`} key={i}>
                                 <td
                                 >{dateHelpers.monthDayAndYear(datum.documentDate)}</td>
                                 <PODropdownMenu
                                    poNumber={datum.poNumber}
                                    alignTop={true}
                                    attachmentCount={datum.poAttachmentCount ?? 0}
                                    showAddContainer
                                    showAddCost={currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isPurchasingEdit}
                                    itemNumber={datum.itemNumber}
                                    containers={datum.containers ?? ""}
                                    callbackRefresh={() => refreshData()}
                                 />
                                 <td>{datum.containers}</td>
                                 <td>{datum.lineStatusText}</td>
                                 <ItemDropdownMenu
                                    itemNumber={datum.itemNumber}
                                    itemDescription={datum.itemWithDescription}
                                    attachmentCount={datum.itemAttachmentCount}
                                    alignTop={true}
                                 />
                                 <td>{datum.vendorItemWithDescription}</td>
                                 <td>{datum.location}</td>
                                 <td>{helpers.formatWithDecimalPlaces(datum.orderQty, 0)}</td>
                                 <td>{datum.qtyShipped.toLocaleString()}</td>
                                 <td>{helpers.formatCurrency(datum.unitCost)}</td>
                                 <td>{helpers.formatCurrency(datum.extendedCost)}</td>
                                 <td>{datum.statusGroupText}</td>
                              </tr>
                           )
                        })}
                     </tbody>
                     <tfoot className="table-footer">
                        <tr>
                           <td colSpan={7} />
                           <td className="fw-bold">{_.sumBy(data, datum => datum.orderQty).toLocaleString()}</td>
                           <td className="fw-bold">{_.sumBy(data, datum => datum.qtyShipped).toLocaleString()}</td>
                           <td />
                           <td className="fw-bold">{helpers.formatCurrency(_.sumBy(data, datum => datum.extendedCost))}</td>
                           <td />
                        </tr>
                     </tfoot>
                  </Table>
               </>
               : <div className="h5">No Open POs found</div>}
         </ServiceThreadSpinner>
      </>
   );
}